import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import TopBar from '../components/TopBar';
import ConciergeLauncher from '../components/ConciergeLauncher';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { TrendingDown, TrendingUp, ArrowRight } from 'lucide-react';

const InvestPage = () => {
  const { showcases, resources } = useData();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    region: ['East Africa'],
    capacity: ['>10 MWh'],
    offtake: ['Utility offtake']
  });

  const investorShowcases = showcases.filter(s => s.openForCoInvestment);

  const toggleFilter = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }));
  };

  const removeFilter = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].filter(v => v !== value)
    }));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <div className="mb-8">
          <span className="eyebrow">Investor · Discover</span>
          <h2 className="mt-3 mb-2">Bankable grid opportunities, matched to your mandate</h2>
          <p style={{ color: 'hsl(var(--ink-muted))' }}>
            Read the market, screen credible projects, and reach the developers behind them.
          </p>
        </div>

        {/* Filter Strip */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(filters).map(([category, values]) =>
            values.map(value => (
              <Badge
                key={`${category}-${value}`}
                variant="secondary"
                className="cursor-pointer"
                onClick={() => removeFilter(category, value)}
              >
                {value} ✕
              </Badge>
            ))
          )}
          <Button variant="ghost" size="sm">+ Add filter</Button>
        </div>

        {/* Market Intelligence Strip */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="p-5">
            <div className="text-xs font-medium mb-1" style={{ color: 'hsl(var(--ink-muted))' }}>BESS pack price (4hr)</div>
            <div className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>$211/kWh</div>
            <div className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--verdant))' }}>
              <TrendingDown className="h-3 w-3" />
              8% YoY · BloombergNEF
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs font-medium mb-1" style={{ color: 'hsl(var(--ink-muted))' }}>Open opportunities</div>
            <div className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{investorShowcases.length}</div>
            <div className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--mint))' }}>
              <TrendingUp className="h-3 w-3" />
              3 new this month
            </div>
          </Card>
          <Card className="p-5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, hsl(var(--surface)) 0%, hsl(48 93% 97%) 100%)', borderColor: 'hsl(var(--accent-soft))' }}>
            <div className="text-xs font-medium mb-1" style={{ color: 'hsl(var(--accent-ink))' }}>✦ Premium · subscription</div>
            <div className="text-base font-semibold mb-1">Full market intelligence</div>
            <button className="text-xs font-semibold" style={{ color: 'hsl(var(--accent-ink))' }}>Unlock benchmarks & forecasts →</button>
          </Card>
        </div>

        {/* Deal Board */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {investorShowcases.map(showcase => (
            <Card
              key={showcase.id}
              className="p-5 cursor-pointer hover:shadow-lg"
              onClick={() => navigate(`/showcase/${showcase.id}`)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="mb-1">{showcase.title}</h4>
                  <div className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>{showcase.location}</div>
                </div>
                {showcase.mandateMatch > 0 && (
                  <Badge variant="secondary" style={{ backgroundColor: 'hsl(var(--mint))', color: 'hsl(var(--ink))' }}>
                    {showcase.mandateMatch}% match
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>Capacity</div>
                  <div className="font-semibold">{showcase.capacity} {showcase.capacityUnit}</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>Offtake</div>
                  <div className="font-semibold text-xs">{showcase.offtakeType}</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>Stage</div>
                  <div className="font-semibold">{showcase.stage}</div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))' }}>
                  ◆ Co-investment
                </span>
                <ArrowRight className="h-4 w-4" style={{ color: 'hsl(var(--ink-muted))' }} />
              </div>
            </Card>
          ))}
        </div>
      </div>

      <ConciergeLauncher />
    </div>
  );
};

export default InvestPage;
